import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { DENIALS, type Denial, type DenyReason } from '@/api/denials';
import type { Failure } from '@/api/errors';
import { PageHeader, useFrameMaybe } from '@/app/frame';
import { Badge, Button, Kicker, TextInput } from './primitives';
import { plainWords, useTechnical } from '@/app/technical';
import { remedy } from '@/api/errors';
import { Refusal, TONE_BORDER, liftsAt } from './Refusal';
import { ContextPanel, Explainer } from './context';
import { explainDenial, leadFor } from './refusal-explain';
import { useWhoMaybe } from './who';

/**
 * A refusal as a whole page (P:289-318): what refused, who can move it, a
 * way to ask them, and every other reason the gateway can give beneath it.
 *
 * The catalogue is shown because a refusal names the rule that refused and
 * never what would have been allowed. Seeing the whole list is how a person
 * learns where the lines are without anyone having to probe them.
 */

const CODES = Object.keys(DENIALS) as DenyReason[];

const SWATCH: Record<Denial['tone'], string> = {
  danger: 'bg-danger',
  warning: 'bg-warning',
  neutral: 'bg-control',
};

const TONE_WORD: Record<Denial['tone'], string> = {
  danger: 'Crosses a line',
  warning: 'Needs a grant',
  neutral: 'The rule',
};

function isCode(code: string | null): code is DenyReason {
  return code !== null && Object.prototype.hasOwnProperty.call(DENIALS, code);
}

/**
 * "Nobody — wait" is an answer, not an addressee. Offering to ask nobody
 * would be a button that sends a message to no one, so those refusals say
 * who instead and offer nothing.
 */
function askable(who: string): boolean {
  return !/^nobody\b/i.test(who.trim());
}

const lowerFirst = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);

export function RefusalPage({ failure }: { failure: Failure }) {
  const frame = useFrameMaybe();
  // Null when the shell itself was refused: then the rule's words stand.
  const people = useWhoMaybe();
  // The gateway's own words, its reason code and the list of every reason
  // are the superadmin's; everyone else reads what happened and who moves it.
  const tech = useTechnical();
  if (failure.kind !== 'denied') {
    // Only a policy decision has a rule and an owner. Anything else — offline,
    // a server fault, a missing setting — is shown as the panel says it.
    return (
      <section aria-label="Refused" className="flex flex-col gap-5">
        <Refusal failure={failure} />
        {tech && <Catalogue current={null} />}
      </section>
    );
  }
  const { tone } = failure.denial;
  // "Department lead" names the session department's lead, when there is one.
  const lead = leadFor(failure.denial, true, people);
  const who = lead ? (lead.name === 'You' ? 'the superadmin' : lead.name) : failure.denial.who;
  const whom = lead ? who : lowerFirst(who);
  const title = tech ? failure.denial.title : plainWords(failure.denial.title);
  const body = tech ? failure.denial.body : remedy(failure, false);
  return (
    <section aria-label="Refused" className="flex flex-col gap-5">
      <div role="alert" className={`flex flex-col gap-3 rounded-brand border p-[22px] ${TONE_BORDER[tone]}`}>
        <span className="flex items-center gap-2.5">
          <span aria-hidden className={`block h-2.5 w-2.5 shrink-0 ${SWATCH[tone]}`} />
          <Kicker tone={tone === 'danger' ? 'danger' : undefined}>Refused</Kicker>
        </span>
        <p className="m-0 text-panel font-semibold">{title}</p>
        <p className="m-0 max-w-[62ch] text-body leading-relaxed">{body}</p>
        {tech && failure.detail && (
          <p className="m-0 max-w-[62ch] text-dense text-secondary">The gateway said: {failure.detail}</p>
        )}
        {failure.resumesAt && (
          <p className="m-0 text-dense text-ink" data-testid="refusal-resumes">
            It opens again {liftsAt(failure.resumesAt)}.
          </p>
        )}
        <div className="flex flex-wrap items-center gap-2.5">
          {askable(who) ? (
            <>
              <Button icon="ask"
                variant="primary"
                disabled={!frame}
                onClick={() => frame?.ask(`I hit “${lowerFirst(title)}”. Ask ${whom} for it on my behalf: `)}
              >Ask for it</Button>
              {who && <span className="text-dense text-meta">Goes to {who}{lead && lead.name !== 'You' && lead.role ? `, who ${lead.role}` : ''}</span>}
            </>
          ) : (
            who && <span className="text-dense text-meta">{who}</span>
          )}
        </div>
        {tech && (
          <span className="text-label text-meta">
            reason <code className="font-mono">{failure.reason}</code>
          </span>
        )}
      </div>
      <Explainer
        label="What would fix this?"
        context={explainDenial(failure.denial, { instance: true, who: people, resumesAt: failure.resumesAt ?? null, ...(tech && isCode(failure.reason) ? { to: `/refusals?code=${failure.reason}` } : {}) })}
      />
      {tech && <Catalogue current={failure.reason} />}
    </section>
  );
}

/** The whole list under a refusal, each line leading to its own entry in /refusals. */
function Catalogue({ current }: { current: string | null }) {
  // One press away rather than always on screen: the refusal itself is what
  // the page is for, and the list would repeat its title back.
  const [open, setOpen] = useState(false);
  if (!open) {
    return (
      <div>
        <Button variant="tertiary" aria-expanded={false} onClick={() => setOpen(true)}>
          Every reason the gateway can give
        </Button>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-3">
      <Kicker>Every reason the gateway can give</Kicker>
      <p className="m-0 max-w-[62ch] text-dense text-meta">
        A refusal always names the rule that refused, and never what would have been allowed — an agent that gets an
        opaque no cannot correct itself, and one that learns what is permitted can probe.
      </p>
      <ul className="m-0 flex list-none flex-col gap-px border border-line bg-line p-0">
        {CODES.map((code) => (
          <li key={code}>
            <Link
              to={`/refusals?code=${code}`}
              aria-current={code === current ? 'true' : undefined}
              className={`grid min-h-tap items-baseline gap-x-4 gap-y-1 px-4 py-[11px] no-underline hover:bg-raised md:grid-cols-[minmax(0,210px)_minmax(0,1fr)] ${
                code === current ? 'bg-sunken' : 'bg-page'
              }`}
            >
              <span className={`text-dense ${code === current ? 'text-ink' : 'text-secondary'}`}>{DENIALS[code].title}</span>
              <span className="break-all font-mono text-label text-meta">{code}</span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * /refusals — the catalogue on its own, linkable to one entry by `?code=`.
 * The list is `DENIALS`, which a test holds equal to the policy's own
 * union, so this page cannot fall behind what the gateway can say.
 */
export function Refusals() {
  const [params, setParams] = useSearchParams();
  const code = params.get('code');
  const [q, setQ] = useState('');
  const selected = isCode(code) ? code : null;
  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return CODES;
    return CODES.filter((c) => c.includes(needle.replace(/\s+/g, '_')) || DENIALS[c].title.toLowerCase().includes(needle));
  }, [q]);

  // Arriving by a link, the entry it names should be in view. Only then: a
  // click inside the list is already where the reader is looking.
  const arrived = useRef(selected);
  const selectedRef = useRef<HTMLLIElement | null>(null);
  useEffect(() => {
    if (arrived.current) selectedRef.current?.scrollIntoView?.({ block: 'center' });
  }, []);

  return (
    <div className="mx-auto flex w-full max-w-[1040px] flex-col gap-6 p-6 phone:p-4">
      <PageHeader kicker="Oversight" title="Every reason the gateway can give" />
      <p className="m-0 max-w-[66ch] text-body text-secondary">
        When the gateway refuses a call it names the rule that refused — one of these {CODES.length} — and never what would
        have been allowed. Each says what it means and who can move it; press one to see what would fix it.
      </p>
      <label className="flex flex-col gap-2">
        <span className="text-micro font-medium uppercase tracking-label text-meta">Find a reason</span>
        <TextInput
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="scope_not_held, or “too fast”"
          aria-label="Find a reason"
        />
      </label>
      {code !== null && !selected && (
        <p className="m-0 text-dense text-meta" data-testid="unknown-code">
          <code className="break-all font-mono">{code}</code> is not a reason the gateway gives. Anything it refuses with
          is on this list; a code that is not came from somewhere else.
        </p>
      )}
      {shown.length === 0 ? (
        <p className="m-0 text-dense text-meta">No reason matches “{q.trim()}”.</p>
      ) : (
        <ul aria-label="Reasons" className="m-0 flex list-none flex-col gap-px border border-line bg-line p-0">
          {shown.map((c) => {
            const d: Denial = DENIALS[c];
            const on = c === selected;
            return (
              <li key={c} ref={on ? selectedRef : undefined} data-testid={`reason-${c}`} data-selected={on || undefined}>
                <button
                  type="button"
                  aria-pressed={on}
                  onClick={() =>
                    setParams(
                      (p) => {
                        const next = new URLSearchParams(p);
                        if (on) next.delete('code');
                        else next.set('code', c);
                        return next;
                      },
                      { replace: true },
                    )
                  }
                  className={`grid min-h-tap w-full items-baseline gap-x-4 gap-y-1.5 border-l-2 px-4 py-3 text-left hover:bg-raised md:grid-cols-[minmax(0,210px)_minmax(0,1fr)_auto] ${
                    on ? 'border-l-accent bg-sunken' : 'border-l-transparent bg-page'
                  }`}
                >
                  <code className={`break-all font-mono text-dense ${on ? 'text-ink' : 'text-meta'}`}>{c}</code>
                  <span className="flex min-w-0 flex-col gap-1">
                    <span className="text-dense font-semibold text-ink">{d.title}</span>
                    <span className="max-w-[62ch] text-dense text-secondary">{d.body}</span>
                    <span className="text-dense text-meta">
                      {askable(d.who) ? `Moved by ${d.who}` : d.who}
                    </span>
                  </span>
                  <span className="justify-self-start md:justify-self-end">
                    <Badge plain tone={d.tone === 'danger' ? 'danger' : d.tone === 'warning' ? 'warning' : 'neutral'}>
                      {TONE_WORD[d.tone]}
                    </Badge>
                  </span>
                </button>
                {on && (
                  <div className="bg-page p-4">
                    <ContextPanel context={explainDenial(d, { instance: false })} />
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
